import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Close, Search } from "@similarweb/icons";
import { SegmentedControl } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Flex } from "@similarweb/ui-components/foundations";

import { PlaybooksNewButton } from "./PlaybooksNewButton";

const SEGMENTS = ["all", "shared", "private"] as const;
export type PlaybooksSegment = (typeof SEGMENTS)[number];

type PlaybooksPageToolbarProps = {
    canCreateShared: boolean;
    sharedCount: number;
    privateCount: number;
    onSegmentChange: (segment: PlaybooksSegment) => void;
    searchTerm: string;
    onSearchChange: (value: string) => void;
    onNewPlaybook: (isSharedWithAccount: boolean) => void;
};

export const PlaybooksPageToolbar = ({
    canCreateShared,
    sharedCount,
    privateCount,
    onSegmentChange,
    searchTerm,
    onSearchChange,
    onNewPlaybook,
}: PlaybooksPageToolbarProps) => {
    const { translate } = useTranslate();
    const [segmentIndex, setSegmentIndex] = useState(0);

    const segmentDisabled: Record<PlaybooksSegment, boolean> = {
        all: sharedCount + privateCount === 0,
        shared: sharedCount === 0,
        private: privateCount === 0,
    };

    const segmentValues = SEGMENTS.map((value) => ({
        value,
        label: translate(`si.playbooks.segment.${value}`),
        disabled: segmentDisabled[value],
    }));

    const handleSegmentChange = (index: number) => {
        setSegmentIndex(index);
        onSegmentChange(SEGMENTS[index]);
    };

    return (
        <Flex
            justifyContent="space-between"
            alignItems="center"
            gap="16px"
        >
            <Flex
                alignItems="center"
                gap="16px"
            >
                <SegmentedControl
                    values={segmentValues}
                    defaultSelectedIndex={segmentIndex}
                    onChange={handleSegmentChange}
                />
                <TextField
                    placeholder={translate("si.playbooks.search.placeholder")}
                    startAddon={<Search size="sm" />}
                    type="text"
                    onChange={onSearchChange}
                    value={searchTerm}
                    data-automation="playbooks-search"
                    endAddon={
                        searchTerm.length > 0 ? (
                            <Close
                                size="sm"
                                onClick={() => onSearchChange("")}
                                cursor="pointer"
                            />
                        ) : null
                    }
                />
            </Flex>
            <PlaybooksNewButton
                canCreateShared={canCreateShared}
                onNewPlaybook={onNewPlaybook}
            />
        </Flex>
    );
};
